import { useMutation } from '@tanstack/react-query';
import { AI_FEEDBACK, AI_FEEDBACK_LABELS, type AiFeedback } from '@raro/domain';
import { Label, Serif, Tag } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import type { AiAnswer } from '../lib/types';

const REF = /\[[A-Z]:([0-9a-f]{8}-[0-9a-f-]{27})\]/g;
const KIND = /^(Fato|Observação|Inferência|Hipótese)([^:]{0,30})?:\s*/;

/**
 * O texto da resposta, pronto para ler: os códigos de citação ([C:…]) viram números pequenos que levam à lista "de onde vem",
 * o que veio como lista vira lista, e "Fato:", "Inferência:", "Hipótese:" viram rótulo. Código de algo que não está na lista some.
 */
function AnswerText({ text, ids, answerId }: { text: string; ids: string[]; answerId?: string }) {
  const inline = (t: string, key: string) => {
    const out: React.ReactNode[] = [];
    let last = 0, group: number[] = [];
    const flush = (k: string) => { if (group.length) { out.push(<sup key={k} className="rr-answer__refs">{group.map((n) => <a key={n} href={`#cite-${answerId ?? ''}-${n}`}>{n}</a>)}</sup>); group = []; } };
    for (const m of t.matchAll(REF)) {
      const before = t.slice(last, m.index);
      if (before.trim()) { flush(`${key}-r${last}`); out.push(before.replace(/\s+$/, '')); } 
      const n = ids.indexOf(m[1]!) + 1;
      if (n > 0 && !group.includes(n)) group.push(n);
      last = m.index! + m[0].length;
    }
    flush(`${key}-r${last}`);
    const rest = t.slice(last);
    if (rest.trim()) out.push(rest);
    return out;
  };
  const item = (t: string, key: string) => { const k = KIND.exec(t); return k ? <><span className="rr-answer__kind">{k[1]}{k[2] ?? ''}</span>{inline(t.slice(k[0].length), key)}</> : inline(t, key); };
  // o modelo às vezes devolve a lista numa linha só (" - Fato: … - Observação: …"): separa pelos rótulos
  const lines = text.replace(/\s+-\s+(?=(Fato|Observação|Inferência|Hipótese)\b)/g, '\n- ').split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const blocks: { list: boolean; items: string[] }[] = [];
  for (const l of lines) { const list = /^[-•]\s+/.test(l); const t = l.replace(/^[-•]\s+/, ''); const prev = blocks.at(-1); if (list && prev?.list) prev.items.push(t); else blocks.push({ list, items: [t] }); }
  return (
    <div className="rr-answer__text">
      {blocks.map((b, i) => b.list
        ? <ul key={i} className="rr-answer__list">{b.items.map((t, n) => <li key={n}>{item(t, `${i}-${n}`)}</li>)}</ul>
        : <p key={i}><Serif>{item(b.items[0]!, String(i))}</Serif></p>)}
    </div>
  );
}

export function AnswerCard({ a }: { a: AiAnswer }) {
  const [fb, setFb] = useState<AiFeedback | null>(a.feedback);
  const feedback = useMutation({ mutationFn: (f: AiFeedback) => api.post(`/ai-answers/${a.id}/feedback`, { feedback: f }), onSuccess: (_d, f) => setFb(f) });
  return (
    <div className="rr-answer">
      <div className="rr-row rr-row--between">
        <Label tone="accent">a Machine responde</Label>
        <span className="rr-answer__meta">
          <Tag tone="outline">{a.tier === 'strong' ? 'modelo forte' : 'modelo pequeno'}</Tag>
          {/* quem decidiu o modelo (regra ou modelo de decisão) fica no registro; a tela mostra só o motivo */}
          {a.routing && <span title="por que este modelo foi escolhido">{a.routing.reason}</span>}
          {a.confidence !== null && <span>confiança {Math.round(a.confidence * 100)}%</span>}
        </span>
      </div>
      <AnswerText text={a.answer} ids={a.citations.map((c) => c.id)} answerId={a.id} />
      {a.citations.length > 0 && (
        <div className="rr-stack rr-stack--2">
          <Label>De onde vem</Label>
          {a.citations.map((c, i) => (
            <div key={c.id} id={`cite-${a.id}-${i + 1}`} className="rr-answer__cite">
              <b>{i + 1}</b>
              <span>
                {c.sourceId || c.href ? <Link to={c.href ?? `/fontes/${c.sourceId}`} style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>{c.sourceTitle}</Link> : c.sourceTitle} — {c.why}
                {c.snippet && <span className="rr-faint"> · {c.snippet.replace(/[«»]/g, '').slice(0, 140)}…</span>}
              </span>
            </div>
          ))}
        </div>
      )}
      {a.gaps.length > 0 && <p className="rr-answer__gaps">o que falta: {a.gaps.join(' · ')}</p>}
      <div className="rr-answer__meta">
        <span>{a.model}{a.latencyMs ? ` · ${(a.latencyMs / 1000).toFixed(1)}s` : ''} · isso ajudou?</span>
        <div className="rr-feedback">{AI_FEEDBACK.map((f) => <button key={f} type="button" aria-pressed={fb === f} onClick={() => feedback.mutate(f)}>{AI_FEEDBACK_LABELS[f]}</button>)}</div>
      </div>
    </div>
  );
}
